import axios from "axios";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { UserContext } from "../contexts/UserContext";
import { FundingPackageLock, FundingPackageLockMode } from "./types";

const HEARTBEAT_INTERVAL_MS = 15000;
const POLL_INTERVAL_MS = 5000;
const TAKEOVER_WAIT_SECONDS = 60;

type UseFundingPackageLockResult = {
  lock: FundingPackageLock | null;
  isLockedByMe: boolean;
  isLockedByOtherUser: boolean;
  hasIncomingTakeoverRequest: boolean;
  takeoverRequestedByName?: string | null;
  secondsUntilTakeoverAllowed: number;
  ensureOwnership: () => Promise<boolean>;
  requestTakeover: () => Promise<void>;
  acceptTakeover: () => Promise<void>;
  declineTakeover: () => Promise<void>;
  forceTakeover: () => Promise<void>;
  releaseLock: () => Promise<void>;
  refreshLock: () => Promise<void>;
};

export function useFundingPackageLock(
  fundingPackageId: number | undefined,
  mode: FundingPackageLockMode
): UseFundingPackageLockResult {
  const { user } = useContext(UserContext);
  const [lock, setLock] = useState<FundingPackageLock | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const userEmail = user?.email?.toLowerCase();

  const isLockedByMe = !!lock?.lockedByEmail && !!userEmail && lock.lockedByEmail.toLowerCase() === userEmail;
  const isLockedByOtherUser = !!lock && !isLockedByMe;
  const hasIncomingTakeoverRequest =
    isLockedByMe && !!lock?.takeoverRequestedByEmail && lock.takeoverRequestedByEmail.toLowerCase() !== userEmail;

  const secondsUntilTakeoverAllowed = useMemo(() => {
    if (!lock?.takeoverRequestedAt || !userEmail || lock.takeoverRequestedByEmail?.toLowerCase() !== userEmail) {
      return 0;
    }

    const requestedAt = new Date(lock.takeoverRequestedAt).getTime();
    const secondsElapsed = Math.floor((now - requestedAt) / 1000);
    return Math.max(TAKEOVER_WAIT_SECONDS - secondsElapsed, 0);
  }, [lock?.takeoverRequestedAt, lock?.takeoverRequestedByEmail, now, userEmail]);

  const postLock = useCallback(
    async (url: string) => {
      if (!fundingPackageId) return null;
      const { data } = await axios.post<FundingPackageLock | null>(url, {
        fundingPackageId,
        mode
      });
      setLock(data);
      return data;
    },
    [fundingPackageId, mode]
  );

  const refreshLock = useCallback(async () => {
    if (!fundingPackageId) return;
    const { data } = await axios.get<FundingPackageLock | null>(
      `/api/FundingPackages/packages/GetPackageLock?fundingPackageId=${fundingPackageId}`
    );
    setLock(data);
  }, [fundingPackageId]);

  const releaseLock = useCallback(async () => {
    if (!fundingPackageId) return;
    await axios.post("/api/FundingPackages/packages/ReleasePackageLock", { fundingPackageId });
    setLock(null);
  }, [fundingPackageId]);

  const requestTakeover = useCallback(async () => {
    if (!fundingPackageId) return;
    const { data } = await axios.post<FundingPackageLock | null>(
      "/api/FundingPackages/packages/RequestPackageLockTakeover",
      { fundingPackageId }
    );
    setLock(data);
  }, [fundingPackageId]);

  const acceptTakeover = useCallback(async () => {
    if (!fundingPackageId) return;
    const { data } = await axios.post<FundingPackageLock | null>(
      "/api/FundingPackages/packages/AcceptPackageLockTakeover",
      { fundingPackageId }
    );
    setLock(data);
  }, [fundingPackageId]);

  const declineTakeover = useCallback(async () => {
    if (!fundingPackageId) return;
    const { data } = await axios.post<FundingPackageLock | null>(
      "/api/FundingPackages/packages/DeclinePackageLockTakeover",
      { fundingPackageId }
    );
    setLock(data);
  }, [fundingPackageId]);

  const forceTakeover = useCallback(async () => {
    await postLock("/api/FundingPackages/packages/ForcePackageLockTakeover");
  }, [postLock]);

  const ensureOwnership = useCallback(async () => {
    const packageLock = await postLock("/api/FundingPackages/packages/HeartbeatPackageLock");
    if (!packageLock || !userEmail) {
      return false;
    }

    return packageLock.lockedByEmail?.toLowerCase() === userEmail;
  }, [postLock, userEmail]);

  useEffect(() => {
    if (!fundingPackageId || !userEmail) return;
    postLock("/api/FundingPackages/packages/ClaimPackageLock");
  }, [fundingPackageId, postLock, userEmail]);

  useEffect(() => {
    if (!fundingPackageId) return;
    const intervalId = setInterval(refreshLock, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fundingPackageId, refreshLock]);

  useEffect(() => {
    if (!fundingPackageId || !isLockedByMe) return;
    const intervalId = setInterval(
      () => postLock("/api/FundingPackages/packages/HeartbeatPackageLock"),
      HEARTBEAT_INTERVAL_MS
    );
    return () => clearInterval(intervalId);
  }, [fundingPackageId, isLockedByMe, postLock]);

  useEffect(() => {
    const intervalId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    return () => {
      if (fundingPackageId && isLockedByMe) {
        axios.post("/api/FundingPackages/packages/ReleasePackageLock", { fundingPackageId }).catch(() => {});
      }
    };
  }, [fundingPackageId, isLockedByMe]);

  return {
    lock,
    isLockedByMe,
    isLockedByOtherUser,
    hasIncomingTakeoverRequest,
    takeoverRequestedByName: lock?.takeoverRequestedByName,
    secondsUntilTakeoverAllowed,
    ensureOwnership,
    requestTakeover,
    acceptTakeover,
    declineTakeover,
    forceTakeover,
    releaseLock,
    refreshLock
  };
}
